// src/components/homepage_content/LiveMatches.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBetSlip } from '../../context/BetSlipContext';
import { FootballAPI } from '../../services/footballApi';
import MatchCard from '../sports/MatchCard';
import { FiRefreshCw, FiChevronRight, FiActivity, FiEye, FiChevronLeft, FiChevronRight as FiChevronRightIcon } from 'react-icons/fi';

const LiveMatches = () => {
  const [matches, setMatches] = useState([]);
  const [groupedMatches, setGroupedMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [oddsFormat, setOddsFormat] = useState('decimal');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slidesPerView, setSlidesPerView] = useState(3);
  
  const navigate = useNavigate();
  const { betslipCount, bets } = useBetSlip();
  const sliderRef = useRef(null);
  const autoPlayRef = useRef(null);

  // Fetch live matches from API
  const fetchLiveMatches = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await FootballAPI.getLiveFixtures();
      
      if (response.success && response.data) {
        const liveData = response.data;
        
        if (liveData.length > 0) {
          // Get all matches but we'll slide through them
          const formattedMatches = liveData.map(fixture => ({
            ...fixture,
            odds: {
              home: fixture.odds?.home || null,
              draw: fixture.odds?.draw || null,
              away: fixture.odds?.away || null
            },
            isLive: ['1H', 'HT', '2H', 'ET', 'P'].includes(fixture.status),
            status: fixture.status,
            elapsed: fixture.elapsed || 0,
            home_score: fixture.home_score,
            away_score: fixture.away_score
          }));
          
          // Group by league for display
          const grouped = {};
          formattedMatches.forEach(fixture => {
            const leagueId = fixture.league_id;
            if (!grouped[leagueId]) {
              grouped[leagueId] = {
                league_id: leagueId,
                league_name: fixture.league_name,
                league_logo: fixture.league_logo,
                country: fixture.country,
                fixtures: []
              };
            }
            grouped[leagueId].fixtures.push(fixture);
          });
          
          // Sort leagues by priority
          const sortedGroups = Object.values(grouped).sort((a, b) => {
            const priorityA = getLeaguePriority(a.league_id, a.league_name);
            const priorityB = getLeaguePriority(b.league_id, b.league_name);
            return priorityA - priorityB;
          });
          
          setGroupedMatches(sortedGroups);
          setMatches(formattedMatches);
          setLastUpdate(new Date());
          setCurrentSlide(0); // Reset to first slide
        } else {
          setMatches([]);
          setGroupedMatches([]);
          setError('No live matches at the moment');
        }
      } else {
        setError('Failed to load live matches');
      }
    } catch (err) {
      setError('Failed to load live matches');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // League priority function
  const getLeaguePriority = (leagueId, leagueName) => {
    const priorityMap = {
      1: 1,   // FIFA World Cup
      2: 2,   // UEFA Champions League
      39: 3,  // Premier League
      140: 4, // La Liga
      135: 5, // Serie A
      78: 6,  // Bundesliga
      61: 7,  // Ligue 1
    };
    
    if (priorityMap[leagueId]) {
      return priorityMap[leagueId];
    }
    
    const name = leagueName?.toLowerCase() || '';
    if (name.includes('fifa world cup') || name.includes('world cup')) return 1;
    if (name.includes('champions league')) return 2;
    if (name.includes('premier league')) return 3;
    if (name.includes('la liga')) return 4;
    if (name.includes('serie a')) return 5;
    if (name.includes('bundesliga')) return 6;
    if (name.includes('ligue 1')) return 7;
    
    return 999;
  };

  // Handle slide navigation
  const nextSlide = () => {
    const totalSlides = Math.ceil(matches.length / slidesPerView);
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
  };

  const prevSlide = () => {
    const totalSlides = Math.ceil(matches.length / slidesPerView);
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  };

  // Handle dot indicator click
  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  // Update slides per view based on screen size
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) {
        setSlidesPerView(1);
      } else if (window.innerWidth < 1024) {
        setSlidesPerView(2);
      } else {
        setSlidesPerView(3);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-play slides
  useEffect(() => {
    if (matches.length <= slidesPerView) return;

    autoPlayRef.current = setInterval(() => {
      nextSlide();
    }, 5000);

    return () => clearInterval(autoPlayRef.current);
  }, [matches.length, slidesPerView]);

  // Pause auto-play on hover
  const pauseAutoPlay = () => {
    clearInterval(autoPlayRef.current);
  };

  const resumeAutoPlay = () => {
    if (matches.length <= slidesPerView) return;
    clearInterval(autoPlayRef.current);
    autoPlayRef.current = setInterval(() => {
      nextSlide();
    }, 5000);
  };

  // Refresh handler
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchLiveMatches();
  };

  // Handle match card click
  const handleMatchCardClick = (matchId) => {
    navigate(`/match/${matchId}`);
  };

  // Navigate to all live matches
  const handleViewAllLive = () => {
    navigate('/live');
  };

  // Initial load
  useEffect(() => {
    fetchLiveMatches();
  }, [fetchLiveMatches]);

  // Get current slide matches
  const getCurrentMatches = () => {
    const start = currentSlide * slidesPerView;
    const end = start + slidesPerView;
    return matches.slice(start, end);
  };

  const currentMatches = getCurrentMatches();
  const totalSlides = Math.ceil(matches.length / slidesPerView);
  const hasMultipleSlides = totalSlides > 1;

  // Loading state
  if (loading) {
    return (
      <div className="bg-gray-900 rounded-xl p-4 sm:p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
            <h2 className="text-xl font-bold text-white">Live Matches</h2>
          </div>
        </div>
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-800 rounded w-1/4"></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="h-48 bg-gray-800 rounded"></div>
            <div className="h-48 bg-gray-800 rounded"></div>
            <div className="h-48 bg-gray-800 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-900 rounded-xl p-4 sm:p-6 border border-gray-800">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <FiActivity className="text-red-500" />
            Live Matches
          </h2>
          {matches.length > 0 && (
            <span className="bg-red-500/20 text-red-400 text-xs px-2 py-1 rounded-full">
              {matches.length} live
            </span>
          )}
          {lastUpdate && (
            <span className="text-xs text-gray-500 hidden sm:inline">
              Updated: {lastUpdate.toLocaleTimeString()}
            </span>
          )}
        </div>
        
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-1 px-3 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg text-sm transition-colors"
          >
            <FiRefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
          
          {matches.length > 0 && (
            <button
              onClick={handleViewAllLive}
              className="flex items-center gap-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm transition-colors"
            >
              <FiEye className="w-4 h-4" />
              <span>View All</span>
              <FiChevronRight className="w-4 h-4" />
            </button>
          )}
          
          <button
            onClick={() => navigate('/betslip')}
            className="relative px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors"
          >
            Betslip
            {betslipCount > 0 && (
              <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {betslipCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-900/30 border border-red-700 rounded-lg p-4 mb-4">
          <p className="text-red-400 text-sm">{error}</p>
          <button
            onClick={handleRefresh}
            className="mt-2 text-red-300 hover:text-red-200 text-sm underline"
          >
            Try again
          </button>
        </div>
      )}

      {/* Slider Section */}
      {matches.length === 0 ? (
        <div className="text-center py-8 sm:py-12">
          <div className="text-5xl mb-4">📺</div>
          <h3 className="text-white font-bold text-lg mb-2">No Live Matches</h3>
          <p className="text-gray-400 text-sm">Check back later for live action</p>
          <button
            onClick={() => navigate('/sports')}
            className="mt-4 text-blue-400 hover:text-blue-300 text-sm"
          >
            Browse upcoming matches →
          </button>
        </div>
      ) : (
        <div 
          className="relative"
          onMouseEnter={pauseAutoPlay}
          onMouseLeave={resumeAutoPlay}
        >
          {/* Slider Container */}
          <div className="overflow-hidden">
            <div 
              className="flex transition-transform duration-500 ease-in-out"
              style={{ 
                transform: `translateX(-${currentSlide * (100 / slidesPerView)}%)`
              }}
            >
              {matches.map((match) => (
                <div 
                  key={match.id}
                  className="flex-shrink-0 px-2"
                  style={{ width: `${100 / slidesPerView}%` }}
                >
                  <div 
                    onClick={() => handleMatchCardClick(match.id)}
                    className="cursor-pointer hover:scale-[1.02] transition-transform"
                  >
                    <MatchCard
                      match={match}
                      sport="football"
                      oddsFormat={oddsFormat}
                      viewMode="grid"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Arrows */}
          {hasMultipleSlides && (
            <>
              <button
                onClick={prevSlide}
                className="absolute left-0 top-1/2 -translate-y-1/2 -ml-3 sm:-ml-4 bg-black/70 hover:bg-black/90 text-white rounded-full p-1.5 sm:p-2 transition-colors z-10 border border-white/10"
                aria-label="Previous slide"
              >
                <FiChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
              <button
                onClick={nextSlide}
                className="absolute right-0 top-1/2 -translate-y-1/2 -mr-3 sm:-mr-4 bg-black/70 hover:bg-black/90 text-white rounded-full p-1.5 sm:p-2 transition-colors z-10 border border-white/10"
                aria-label="Next slide"
              >
                <FiChevronRightIcon className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            </>
          )}
          {/* View All Button */}
          {matches.length > 3 && (
            <div className="flex justify-center mt-6">
              <button
                onClick={handleViewAllLive}
                className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors group text-sm"
              >
                <FiEye className="w-4 h-4" />
                <span>View All Live Matches</span>
                <FiChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          )}
        </div>
      )}   
    </div>
  );
};

export default LiveMatches;